import { type QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { z } from 'zod';

import { fetchCurrentUser } from './auth/hooks';
import { AppShell } from './components/layout/app-shell';
import { parseListQuerySearch, stringifyListQuerySearch } from './lib/list-query';
import { RootLayout } from './routes/__root';
import { AccountDetailPage } from './routes/accounts/$accountId';
import { AccountsListPage } from './routes/accounts/index';
import { AuditPage } from './routes/audit/index';
import { CampaignDetailPage } from './routes/campaigns/$campaignId';
import { CampaignsListPage } from './routes/campaigns/index';
import { ClientDetailPage } from './routes/clients/$clientId';
import { ClientsListPage } from './routes/clients/index';
import { DashboardPage } from './routes/index';
import { LoginPage } from './routes/login';
import { ReplenishmentPage } from './routes/replenishment/index';
import { TradeLinkDetailPage } from './routes/trade-links/$tradeLinkId';
import { TradeLinksListPage } from './routes/trade-links/index';
import { UsersPlaceholderPage } from './routes/users/index';
import { WarehouseDetailPage } from './routes/warehouses/$warehouseId';
import { WarehousesListPage } from './routes/warehouses/index';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  component: AppShell,
  beforeLoad: async ({ context }) => {
    const user = await fetchCurrentUser(context.queryClient);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (!user) throw redirect({ to: '/login' });
    return { user };
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: DashboardPage,
  staticData: { breadcrumb: 'Главная' },
});

const clientsListSearchSchema = z.object({
  filter: z
    .object({
      includeDeleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const clientsListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/clients',
  component: ClientsListPage,
  staticData: { breadcrumb: 'Клиенты' },
  validateSearch: (raw: Record<string, unknown>) => clientsListSearchSchema.parse(raw),
});

const clientDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/clients/$clientId',
  component: ClientDetailPage,
  staticData: { breadcrumb: 'Подробности' },
});

const warehousesListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/warehouses',
  component: WarehousesListPage,
  staticData: { breadcrumb: 'Склады' },
});

const warehouseDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/warehouses/$warehouseId',
  component: WarehouseDetailPage,
  staticData: { breadcrumb: 'Подробности' },
});

const AccountStatusEnum = z.enum(['warming_up', 'available', 'cooldown', 'disabled', 'banned']);
const AccountRoleEnum = z.enum(['storage', 'sender']);
const accountsListSearchSchema = z.object({
  filter: z
    .object({
      role: AccountRoleEnum.optional(),
      status: AccountStatusEnum.optional(),
      includeDeleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const accountsListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounts',
  component: AccountsListPage,
  staticData: { breadcrumb: 'Аккаунты' },
  validateSearch: (raw: Record<string, unknown>) => accountsListSearchSchema.parse(raw),
});

const accountDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounts/$accountId',
  component: AccountDetailPage,
  staticData: { breadcrumb: 'Подробности' },
});

const tradeLinksListSearchSchema = z.object({
  filter: z
    .object({
      clientId: z.string().uuid().optional(),
      includeDeleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const tradeLinksListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/trade-links',
  component: TradeLinksListPage,
  staticData: { breadcrumb: 'Trade-links' },
  validateSearch: (raw: Record<string, unknown>) => tradeLinksListSearchSchema.parse(raw),
});

const tradeLinkDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/trade-links/$tradeLinkId',
  component: TradeLinkDetailPage,
  staticData: { breadcrumb: 'Подробности' },
});

// Phase 8.5-05 — list-screen search schemas. Each list route owns the
// shape of its `?filter[…]&sort=…&page=…&pageSize=…` query state. UI
// reads via `useListQueryRoute`; invalid params surface to TanStack's
// error boundary.
const CampaignStatusEnum = z.enum(['draft', 'running', 'paused', 'cancelled', 'completed']);
const campaignsListSearchSchema = z.object({
  filter: z
    .object({
      clientId: z.string().uuid().optional(),
      status: CampaignStatusEnum.optional(),
      includeDeleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const campaignsListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/campaigns',
  component: CampaignsListPage,
  staticData: { breadcrumb: 'Кампании' },
  validateSearch: (raw: Record<string, unknown>) => campaignsListSearchSchema.parse(raw),
});

// Phase 8.5-05 — `recipients-table` reads its list query state from this
// route's URL via `useListQueryRoute`. The schema describes the bracketed
// filter shape and coerces page/pageSize from string → number.
const RecipientStatusEnum = z.enum([
  'pending',
  'enqueued',
  'sent',
  'completed',
  'quarantined',
  'resolved',
]);
const campaignDetailSearchSchema = z.object({
  // Plan 08-03 — `types` is a CSV of event-type groups consumed by
  // <LogsPanel> via `useListQueryRoute`. Both list-screens (recipients
  // + events) share the same `filter.*` namespace because TanStack
  // Router only supports one `validateSearch` per route. The two
  // sub-features rarely toggle filters simultaneously, so the cohabitation
  // is acceptable.
  filter: z
    .object({
      status: RecipientStatusEnum.optional(),
      types: z.string().optional(),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const campaignDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/campaigns/$campaignId',
  component: CampaignDetailPage,
  staticData: { breadcrumb: 'Подробности' },
  validateSearch: (raw: Record<string, unknown>) => campaignDetailSearchSchema.parse(raw),
});

const usersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/users',
  component: UsersPlaceholderPage,
  staticData: { breadcrumb: 'Пользователи' },
});

// Plan 08-03 UI-04 — system-wide audit log page. Filters: types (CSV),
// actorId (uuid), targetType (string).
const auditSearchSchema = z.object({
  filter: z
    .object({
      types: z.string().optional(),
      actorId: z.string().optional(),
      targetType: z.string().optional(),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const auditRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/audit',
  component: AuditPage,
  staticData: { breadcrumb: 'Аудит' },
  validateSearch: (raw: Record<string, unknown>) => auditSearchSchema.parse(raw),
});

// Plan 10-03 — replenishment-transfers history page. Filter UUIDs are
// validated permissively (string with len bounds) so the operator can
// type-paste partial IDs without UI errors; backend rejects malformed
// UUIDs with a 400.
const ReplenishmentStatusEnum = z.enum(['pending', 'sent', 'accepted', 'failed', 'cancelled']);
const replenishmentSearchSchema = z.object({
  filter: z
    .object({
      warehouseId: z.string().min(1).max(64).optional(),
      storageAccountId: z.string().min(1).max(64).optional(),
      senderAccountId: z.string().min(1).max(64).optional(),
      status: ReplenishmentStatusEnum.optional(),
      from: z.string().min(1).max(64).optional(),
      to: z.string().min(1).max(64).optional(),
    })
    .optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(200).optional(),
});

const replenishmentRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/replenishment',
  component: ReplenishmentPage,
  staticData: { breadcrumb: 'Пополнение' },
  validateSearch: (raw: Record<string, unknown>) => replenishmentSearchSchema.parse(raw),
});

const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_public',
});

const loginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: async ({ context }) => {
    const user = await fetchCurrentUser(context.queryClient);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (user) throw redirect({ to: '/' });
  },
});

const routeTree = rootRoute.addChildren([
  protectedLayoutRoute.addChildren([
    dashboardRoute,
    clientsListRoute,
    clientDetailRoute,
    warehousesListRoute,
    warehouseDetailRoute,
    accountsListRoute,
    accountDetailRoute,
    tradeLinksListRoute,
    tradeLinkDetailRoute,
    campaignsListRoute,
    campaignDetailRoute,
    replenishmentRoute,
    auditRoute,
    usersRoute,
  ]),
  publicLayoutRoute.addChildren([loginRoute]),
]);

export const buildRouter = (queryClient: QueryClient, history?: RouterHistory) =>
  createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Phase 8.5-05 — bracket-format search params (ADR-014). Same shape
    // as backend wire format and `buildListQuerySerializer`, so URL state
    // and API requests stay in lockstep.
    parseSearch: parseListQuerySearch,
    stringifySearch: stringifyListQuerySearch,
    ...(history ? { history } : {}),
  });

export type AppRouter = ReturnType<typeof buildRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
